import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { apiFetch } from './client';

export interface DateRange {
  from?: string;
  to?: string;
}

export interface SummaryReport {
  from: string;
  to: string;
  currency: string;
  txnCount: number;
  canceledCount: number;
  pendingCount: number;
  grossCents: number;
  refundedCents: number;
  netCents: number;
  disputeCount: number;
  disputedAmountCents: number;
  tenderSplit: {
    card_present: { count: number; grossCents: number };
    keyed: { count: number; grossCents: number };
    cash: { count: number; grossCents: number };
  };
  cardBrandSplit: Record<string, { count: number; grossCents: number }>;
  averageSaleCents: number;
}

export interface ByProductRow {
  productId: string | null;
  name: string;
  categoryId: string | null;
  qtySold: number;
  grossCents: number;
  refundedCents: number;
  netCents: number;
}

export interface ByCategoryRow {
  /** Per-event category UUID; null for the synthetic Custom bucket. */
  categoryId: string | null;
  /** Display name from the categories table; "Custom" fallback for items
   *  with no product or whose category was deleted. */
  categoryName: string;
  qtySold: number;
  grossCents: number;
  refundedCents: number;
  netCents: number;
}

export interface TimelineRow {
  bucketStart: string;
  txnCount: number;
  grossCents: number;
  refundedCents: number;
  netCents: number;
}

export interface TransactionRow {
  id: string;
  succeededAt: string | null;
  createdAt: string;
  canceledAt: string | null;
  amountCents: number;
  refundedAmountCents: number;
  netCents: number;
  status: 'pending' | 'succeeded' | 'canceled' | 'voided';
  tenderType: 'card_present' | 'keyed' | 'cash';
  voidedAt?: string | null;
  cardBrand: string | null;
  cardLast4: string | null;
  description: string | null;
  deviceLabel: string | null;
  disputedAt: string | null;
  disputeId: string | null;
  stripePaymentIntentId: string | null;
}

export interface DisputeRow {
  id: string;
  succeededAt: string | null;
  amountCents: number;
  refundedAmountCents: number;
  cardBrand: string | null;
  cardLast4: string | null;
  disputedAt: string;
  disputeId: string;
  stripePaymentIntentId: string | null;
  description: string | null;
}

function rangeQuery(range: DateRange): string {
  const params = new URLSearchParams();
  if (range.from) params.set('from', range.from);
  if (range.to) params.set('to', range.to);
  return params.toString();
}

export function useReportSummary(eventId: string, range: DateRange) {
  return useQuery({
    queryKey: ['event', eventId, 'reports', 'summary', range],
    queryFn: () =>
      apiFetch<SummaryReport>(
        `/api/v1/admin/events/${eventId}/reports/summary?${rangeQuery(range)}`,
      ),
    enabled: Boolean(eventId),
  });
}

export function useReportByProduct(eventId: string, range: DateRange) {
  return useQuery({
    queryKey: ['event', eventId, 'reports', 'by-product', range],
    queryFn: () =>
      apiFetch<{ rows: ByProductRow[] }>(
        `/api/v1/admin/events/${eventId}/reports/by-product?${rangeQuery(range)}`,
      ).then((r) => r.rows),
    enabled: Boolean(eventId),
  });
}

export function useReportByCategory(eventId: string, range: DateRange) {
  return useQuery({
    queryKey: ['event', eventId, 'reports', 'by-category', range],
    queryFn: () =>
      apiFetch<{ rows: ByCategoryRow[] }>(
        `/api/v1/admin/events/${eventId}/reports/by-category?${rangeQuery(range)}`,
      ).then((r) => r.rows),
    enabled: Boolean(eventId),
  });
}

export interface TimelineResponse {
  from: string;
  to: string;
  interval: 'hour' | 'day';
  timezone: string;
  rows: TimelineRow[];
}

export function useReportTimeline(
  eventId: string,
  range: DateRange,
  interval: 'hour' | 'day' = 'day',
) {
  return useQuery({
    queryKey: ['event', eventId, 'reports', 'timeline', range, interval],
    queryFn: () =>
      apiFetch<TimelineResponse>(
        `/api/v1/admin/events/${eventId}/reports/timeline?${rangeQuery(range)}&interval=${interval}`,
      ),
    enabled: Boolean(eventId),
  });
}

export interface TransactionsListParams extends DateRange {
  status?: 'pending' | 'succeeded' | 'canceled' | 'voided';
  tender?: 'card_present' | 'keyed' | 'cash';
  cardBrand?: string;
  cardLast4?: string;
  productId?: string;
  limit?: number;
}

interface TransactionsPage {
  rows: TransactionRow[];
  nextCursor: string | null;
}

/**
 * Paged transactions list with cursor-based infinite scroll. Returns
 * `data.pages` as an array of pages — flatten with
 * `data.pages.flatMap((p) => p.rows)` to render. Call `fetchNextPage()` from
 * an IntersectionObserver attached to a sentinel at the bottom of the table.
 */
export function useReportTransactions(eventId: string, params: TransactionsListParams) {
  return useInfiniteQuery<
    TransactionsPage,
    Error,
    { pages: TransactionsPage[]; pageParams: (string | undefined)[] },
    [string, string, string, string, TransactionsListParams],
    string | undefined
  >({
    queryKey: ['event', eventId, 'reports', 'transactions', params],
    initialPageParam: undefined,
    queryFn: ({ pageParam }) => {
      const q = new URLSearchParams();
      if (params.from) q.set('from', params.from);
      if (params.to) q.set('to', params.to);
      if (params.status) q.set('status', params.status);
      if (params.tender) q.set('tender', params.tender);
      if (params.cardBrand) q.set('cardBrand', params.cardBrand);
      if (params.cardLast4) q.set('cardLast4', params.cardLast4);
      if (params.productId) q.set('productId', params.productId);
      if (pageParam) q.set('cursor', pageParam);
      if (params.limit) q.set('limit', String(params.limit));
      return apiFetch<TransactionsPage>(
        `/api/v1/admin/events/${eventId}/reports/transactions?${q.toString()}`,
      );
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: Boolean(eventId),
  });
}

export function useReportDisputes(eventId: string, range: DateRange) {
  return useQuery({
    queryKey: ['event', eventId, 'reports', 'disputes', range],
    queryFn: () =>
      apiFetch<{ rows: DisputeRow[] }>(
        `/api/v1/admin/events/${eventId}/reports/disputes?${rangeQuery(range)}`,
      ).then((r) => r.rows),
    enabled: Boolean(eventId),
  });
}

/** Trigger a CSV file download via the browser. */
export function downloadCsv(
  eventId: string,
  type: 'transactions' | 'items',
  range: DateRange,
): void {
  const q = new URLSearchParams();
  q.set('type', type);
  if (range.from) q.set('from', range.from);
  if (range.to) q.set('to', range.to);
  const url = `/api/v1/admin/events/${eventId}/reports/export.csv?${q.toString()}`;
  // Anchor with download attr triggers save dialog; fetch+blob would also work
  // but anchor lets the browser pick its own filename when none is suggested.
  window.location.href = url;
}
